import { MovieGetConvertResultResponse } from "@/apiRequests/models/communityTalk/movie/movieGetConvertResultResponse";
import MovieApiRequest from "@/apiRequests/resources/communityTalk/movieApiRequest";
import PictureApiRequest from "@/apiRequests/resources/communityTalk/pictureApiRequest";
import S3ApiRequest from "@/apiRequests/resources/s3ApiRequest";

/**
 * 画像・動画投稿処理。
 * Controller層 <-> Domain層[ｺｺ] <-> ApiRequest層
 */
export default class MediaDomain {
	private readonly _pictureApiRequest: PictureApiRequest;
	private readonly _movieApiRequest: MovieApiRequest;
	private readonly _s3ApiRequest: S3ApiRequest;

	/**
	 * コンストラクタ。
	 */
	constructor() {
		this._pictureApiRequest = new PictureApiRequest();
		this._movieApiRequest = new MovieApiRequest();
		this._s3ApiRequest = new S3ApiRequest();
	}

	/**
	 * プロフィール用画像投稿APIへのPOSTリクエスト。
	 */
	public async createProfilePictureData(tenantId: string, file: File): Promise<{ tmpId: string; url: string }> {
		const presignedUrlResponse = await this._pictureApiRequest.getPresignedUrl(tenantId);
		await this._s3ApiRequest.uploadFile(file, presignedUrlResponse.presignedUrl);

		// 署名付きURLから本ファイルパスへ画像を移動
		const pictureCompleteResponse = await this._pictureApiRequest.complete(tenantId, {
			tmpId: presignedUrlResponse.tmpId,
			imageType: "profile",
		});

		return {
			tmpId: presignedUrlResponse.tmpId,
			url: pictureCompleteResponse.url,
		};
	}

	/**
	 * 画像投稿処理。
	 */
	public async createPictureData(tenantId: string, file: File): Promise<{ tmpId: string; url: string }> {
		const presignedUrlResponse = await this._pictureApiRequest.getPresignedUrl(tenantId);
		await this._s3ApiRequest.uploadFile(file, presignedUrlResponse.presignedUrl);

		// 署名付きURLから本ファイルパスへ画像を移動
		const pictureCompleteResponse = await this._pictureApiRequest.complete(tenantId, {
			tmpId: presignedUrlResponse.tmpId,
			imageType: "talk",
		});

		return {
			tmpId: presignedUrlResponse.tmpId,
			url: pictureCompleteResponse.url,
		};
	}

	/**
	 * この回数以上結果取得リクエストを送信した場合は処理を中断。
	 */
	private convertResultMaxCallCount = 30;
	/**
	 * 結果取得リクエスト送信間隔(秒)。
	 */
	private convertResultCallWaitSeconds = 1;
	/**
	 * 動画投稿処理。
	 */
	public async createMovieData(tenantId: string, file: File, displayOrder: number): Promise<{ tmpId: string; thumbnailUrl: string; thumbnailIndex: number }> {
		const presignedUrlResponse = await this._movieApiRequest.getPresignedUrl(tenantId);
		await this._s3ApiRequest.uploadFile(file, presignedUrlResponse.presignedUrl);

		const movieConvertResponse = await this._movieApiRequest.convert(tenantId, {
			tenantId: tenantId,
			tmpId: presignedUrlResponse.tmpId,
			displayOrder: displayOrder,
		});

		let isCompleteConvert = false;
		let convertResultCallCount = 0;
		let movieConvertResult: MovieGetConvertResultResponse = {
			status: "fail",
			thumbnailUrl: "",
			thumbnailIndex: 0,
		};
		while (!isCompleteConvert && convertResultCallCount <= this.convertResultMaxCallCount) {
			movieConvertResult = await this._movieApiRequest.getConvertResult(tenantId, { jobId: movieConvertResponse.jobId });
			switch (movieConvertResult.status) {
				case "success":
					isCompleteConvert = true;
					break;
				case "error":
				case "cancel":
					throw new Error("動画変換処理に失敗しました。");
				case "processing":
				case "submitted":
				case "fail": // APIの内部エラーではジョブが失敗したかどうか不明なため、もう一度リクエストを送付
					convertResultCallCount++;
					break;
				default:
					break;
			}
			await new Promise((resolve) => setTimeout(resolve, this.convertResultCallWaitSeconds * 1000));
		}
		if (!isCompleteConvert) {
			throw new Error("動画変換処理がタイムアウトしました。");
		}
		return {
			tmpId: presignedUrlResponse.tmpId,
			thumbnailUrl: movieConvertResult.thumbnailUrl,
			thumbnailIndex: movieConvertResult.thumbnailIndex,
		};
	}
}
